/**
 * useVoiceCall — kapselt das Twilio Voice JS SDK `Device` fuer Sales-Browser-
 * Anrufe. Genau EIN aktiver Anruf gleichzeitig.
 *
 * Rueckgabe:
 *   status           "idle" | "connecting" | "ringing" | "open" | "error"
 *   activeCustomerId Kunden-ID des laufenden Anrufs (oder null)
 *   durationSec      Gespraechsdauer in Sekunden (ab "accept")
 *   error            letzte Fehlermeldung
 *   isBusy           true waehrend connecting/ringing/open
 *   start({to,customerId})  startet den Anruf (device.connect)
 *   hangup()         legt auf (call.disconnect)
 *
 * Das SDK wird lazy per dynamic import geladen, damit es nicht im initialen
 * Bundle landet und Mikrofon-/WebRTC-Code erst beim ersten Anruf greift.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchVoiceToken } from "../api/voice";

// Deutsche National-/Auslandsformate in E.164 zwingen (DE-Default).
//   00XX… -> +XX…  |  0XXXXXXX -> +49XXXXXXX  |  +… bleibt  |  rohe Ziffern -> +…
function toE164(raw) {
  const s = String(raw || "").replace(/[^\d+]/g, "");
  if (!s) return "";
  if (s.startsWith("+")) return s;
  if (s.startsWith("00")) return "+" + s.slice(2);
  if (s.startsWith("0")) return "+49" + s.slice(1);
  return "+" + s;
}

export function useVoiceCall({ apiBase, token }) {
  const [status, setStatus] = useState("idle");
  const [activeCustomerId, setActiveCustomerId] = useState(null);
  const [durationSec, setDurationSec] = useState(0);
  const [error, setError] = useState("");

  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const cleanupCall = useCallback(() => {
    stopTimer();
    setDurationSec(0);
    setActiveCustomerId(null);
    callRef.current = null;
  }, []);

  const ensureDevice = useCallback(async () => {
    const mod = await import("@twilio/voice-sdk");
    const Device = mod.Device || (mod.default && mod.default.Device);
    const data = await fetchVoiceToken({ apiBase, token });
    if (deviceRef.current) {
      try {
        deviceRef.current.updateToken(data.token);
        return deviceRef.current;
      } catch (_) {
        try {
          deviceRef.current.destroy();
        } catch (_) {}
        deviceRef.current = null;
      }
    }
    const device = new Device(data.token, {
      codecPreferences: ["opus", "pcmu"],
    });
    device.on("tokenWillExpire", async () => {
      try {
        const d = await fetchVoiceToken({ apiBase, token });
        device.updateToken(d.token);
      } catch (_) {}
    });
    device.on("error", (e) => {
      setError((e && e.message) || "Geräte-Fehler");
      setStatus("error");
    });
    deviceRef.current = device;
    return device;
  }, [apiBase, token]);

  const start = useCallback(
    async ({ to, customerId }) => {
      setError("");
      const target = toE164(to);
      if (!target) {
        setError("Keine Telefonnummer hinterlegt");
        setStatus("error");
        return;
      }
      try {
        setStatus("connecting");
        setActiveCustomerId(customerId != null ? customerId : null);
        const device = await ensureDevice();
        const call = await device.connect({
          params: {
            To: target,
            customerId: customerId != null ? String(customerId) : "",
          },
        });
        callRef.current = call;
        call.on("ringing", () => setStatus("ringing"));
        call.on("accept", () => {
          setStatus("open");
          stopTimer();
          const startedAt = Date.now();
          timerRef.current = setInterval(() => {
            setDurationSec(Math.floor((Date.now() - startedAt) / 1000));
          }, 1000);
        });
        call.on("disconnect", () => {
          setStatus("idle");
          cleanupCall();
        });
        call.on("cancel", () => {
          setStatus("idle");
          cleanupCall();
        });
        call.on("reject", () => {
          setStatus("idle");
          cleanupCall();
        });
        call.on("error", (e) => {
          setError((e && e.message) || "Anruf-Fehler");
          setStatus("error");
          cleanupCall();
        });
      } catch (e) {
        setError((e && e.message) || "Anruf fehlgeschlagen");
        setStatus("error");
        cleanupCall();
      }
    },
    [ensureDevice, cleanupCall],
  );

  const hangup = useCallback(() => {
    try {
      if (callRef.current) callRef.current.disconnect();
    } catch (_) {}
    try {
      if (deviceRef.current) deviceRef.current.disconnectAll();
    } catch (_) {}
    setStatus("idle");
    cleanupCall();
  }, [cleanupCall]);

  useEffect(
    () => () => {
      stopTimer();
      try {
        if (callRef.current) callRef.current.disconnect();
      } catch (_) {}
      try {
        if (deviceRef.current) deviceRef.current.destroy();
      } catch (_) {}
    },
    [],
  );

  const isBusy =
    status === "connecting" || status === "ringing" || status === "open";

  return { status, activeCustomerId, durationSec, error, isBusy, start, hangup };
}
